import {
  DROPBOX_DOMAIN,
  S3_BASE_PATH,
  S3_DOMAIN,
} from '@/_constants/documents';
import { downloadDocument } from '@/_api/client-documents';
import { uniq } from '@/_utils/array';
import { isEmpty } from '@/_utils/strings';
import { US_TAX_FORMS } from '@/_constants/us-tax-forms';
import type { DocumentItem, FileType } from '@/_types/models/documents';
import { IconType } from '@/_types/icons';
import { Questionnaire } from '@/_types/models/questionnaire';
import { FirmPreferences } from '@/_types/models/firm';
export interface UploadedResponse {
  document_location: string;
  document_name: string;
}

export const makeDocument = ({
  document_location,
  document_name,
}: UploadedResponse): FileType => {
  const now: string = new Date().toISOString();
  const document: FileType = {
    annotations: [],
    created_at: now,
    description: '',
    extracted_document_id: null,
    link: `${S3_BASE_PATH}/${document_location}`,
    name: document_name,
    questionnaire_id: null,
    tags: [],
    type: 'file',
    updated_at: now,
    deleted_at: null,
  };

  return document;
};

export const getFileItemPath = (link: string): string => {
  const parts = link.split('/');
  let hostIndex = parts.findIndex((part) => part.includes(S3_DOMAIN));
  let rootFolderIndex = hostIndex + 3;

  if (hostIndex === -1) {
    hostIndex = parts.findIndex((part) => part.includes(DROPBOX_DOMAIN));
    rootFolderIndex = hostIndex + 4;
  }

  return '/' + parts.slice(rootFolderIndex).join('/');
};

export const convertToRanges = (array: any[]) => {
  const uniqSorted: Array<any> = uniq(array.sort((a, b) => a - b));
  const ranges = [];

  for (let i = 0; i < uniqSorted.length; i++) {
    const rangeStart = uniqSorted[i];
    let rangeEnd = uniqSorted[i];

    while (uniqSorted[i + 1] - uniqSorted[i] === 1) {
      rangeEnd = uniqSorted[i + 1];
      i++;
    }

    if (rangeStart === rangeEnd) ranges.push(rangeStart);
    else ranges.push(`${rangeStart}-${rangeEnd}`);
  }

  return ranges;
};

export const getChildDocuments = (
  parentDocument: any,
  extractedDocuments: any,
) => {
  return extractedDocuments.filter(
    (doc: any) => doc.parent_document_id === parentDocument.id,
  );
};

/**
 * Normalizes a tax form name for fuzzy matching by removing special characters
 * and converting to uppercase (e.g., "W-2G" → "W2G")
 */
const normalizeTaxFormName = (name: string): string => {
  return name
    .toUpperCase()
    .replace(/[-\s]/g, '') // Remove hyphens and spaces
    .replace(/[^A-Z0-9]/g, ''); // Keep only alphanumeric characters
};

const documentContainsTaxForm = (
  documentName: string,
  taxForm: string,
): boolean => {
  const normalizedDoc = normalizeTaxFormName(documentName);
  const normalizedForm = normalizeTaxFormName(taxForm);

  return normalizedDoc.includes(normalizedForm);
};

// Return the order of the document based on its description & file name for more accurate sorting
export const getDocumentOrder = (doc: any, preferences?: FirmPreferences) => {
  let index = US_TAX_FORMS.length + 1;

  if (!doc) return index;

  const orderName = getOrderName(doc);

  if (preferences && !!preferences.custom_document_export_order?.length) {
    index = preferences.custom_document_export_order.findIndex((form) => {
      const fuzzyMatch = documentContainsTaxForm(orderName, form);
      return (
        orderName.startsWith(form) || orderName.includes(form) || fuzzyMatch
      );
    });

    // If the document is not found in the custom document export order, add it to the end
    if (index === -1) {
      index = preferences.custom_document_export_order.length + 1;
    }
  } else {
    index = US_TAX_FORMS.findIndex((form) => {
      const fuzzyMatch = documentContainsTaxForm(orderName, form);
      return (
        orderName.startsWith(form) || orderName.includes(form) || fuzzyMatch
      );
    });

    // If the document is not found in the US tax forms, add it to the end
    if (index === -1) {
      index = US_TAX_FORMS.length + 1;
    }
  }

  if (
    isEmpty(doc.description) ||
    doc.description.startsWith('Unknown Document')
  ) {
    // If the document is unknown, add it after the US tax forms or custom document export order
    index = US_TAX_FORMS.length + 2;
  }

  return index;
};

export const getDocumentIcon = (
  item?: DocumentItem,
  isOpen?: boolean,
): IconType => {
  if (item?.type === 'file') return 'file';

  const isEmpty: boolean = item?.type === 'folder' && item?.items_count === 0;

  if (item?.name?.includes('STAFF ONLY')) return 'folder-lock';
  if (isEmpty) return isOpen ? 'folder-open' : 'folder';

  return isOpen ? 'folder-fill-open' : 'folder-fill';
};

export const getRealDocumentPath = async (
  questionnaire: Questionnaire,
  path: string,
  token: string,
): Promise<string | undefined> => {
  const extractedDocument = questionnaire?.extracted_documents?.find((ed) =>
    ed.source.endsWith(path),
  );

  const relatedDocument = questionnaire?.related_documents?.find((rd) =>
    rd?.endsWith?.(path),
  );

  // Prefer the extracted document's source to avoid accidental double-prefixing
  // when related_documents already includes the company path and the provided
  // path is absolute. Fall back to relatedDocument if no extractedDocument match.
  const realDocumentUrl: string | undefined =
    extractedDocument?.source || relatedDocument;

  const { document } = await downloadDocument({
    companyId: questionnaire?.company_id!,
    link: realDocumentUrl!,
    token,
  });

  return document?.data?.link;
};

export const getRealDocumentPathWithOriginal = async (
  questionnaire: Questionnaire,
  path: string,
  token: string,
): Promise<{ downloadedLink?: string; originalLink?: string }> => {
  const extractedDocument = questionnaire?.extracted_documents?.find((ed) =>
    ed.source.endsWith(path),
  );

  const relatedDocument = questionnaire?.related_documents?.find((rd) =>
    rd?.endsWith?.(path),
  );

  // Prefer the extracted document's source to avoid accidental double-prefixing
  // when related_documents already includes the company path and the provided
  // path is absolute. Fall back to relatedDocument if no extractedDocument match.
  const realDocumentUrl: string | undefined =
    extractedDocument?.source || relatedDocument;

  const { document } = await downloadDocument({
    companyId: questionnaire?.company_id!,
    link: realDocumentUrl!,
    token,
  });

  return {
    downloadedLink: document?.data?.link,
    originalLink: realDocumentUrl,
  };
};

export const unknownDocumentDescription = () => {
  return 'Add a description';
};

export const getIsClientActionsFolderDisabled = (
  isAdvisor: boolean,
  preferences: FirmPreferences,
  folderBeingCompared: string,
): boolean => {
  try {
    if (isAdvisor) return false;

    const { folders_where_client_actions_disabled } = preferences;

    if (!folders_where_client_actions_disabled) return false;
    if (!Array.isArray(folders_where_client_actions_disabled)) return false;
    if (folders_where_client_actions_disabled.includes('')) return true;

    // Up to 2100 just to be safe
    const years = Array.from({ length: 100 }, (_, i) => 2000 + i);
    const folderBeingComparedArray = folderBeingCompared.split('/');

    return folders_where_client_actions_disabled.some((disabledFolder) => {
      if (folderBeingCompared === disabledFolder) return true;
      if (folderBeingCompared.startsWith(disabledFolder)) return true;

      const disabledFolderArray: Array<string> = disabledFolder.split('/');
      const matchingFolders: Array<boolean> = [];

      disabledFolderArray.forEach((dir: string, index: number) => {
        if (dir === folderBeingComparedArray[index]) {
          matchingFolders.push(true);
        } else if (
          dir === '{year}' &&
          years.includes(Number(folderBeingComparedArray[index]))
        ) {
          matchingFolders.push(true);
        } else {
          matchingFolders.push(false);
        }
      });

      return (
        matchingFolders.length > 0 &&
        matchingFolders.every((folder: boolean) => folder === true)
      );
    });
  } catch (error) {
    console.error(error);
    return false;
  }
};

// Get the name of the document for sorting
const getOrderName = (document: any) => {
  if (
    !!document.description?.length &&
    !['unknown', 'Add a description'].includes(document.description)
  ) {
    return document.description;
  }

  // If the document is unknown, use the file name
  return document.source?.split('/').pop() || '';
};
